import React, { useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Header from '../common/Header'
import { MoreVertical, Send, Eye, EyeOff, User, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { Card, CardTitle, TableHeader, TableCell, Badge, KpiCard } from '../UI'
import inviteStyles from '../../_css/Invite.module.css'
import useRole from '../../hooks/getRole'
import { toCapitalize } from '../helper/formatters'
import SearchableDrp from '../common/SearchableDrp'
import { USER_ROLES } from '../Invite/constants'
import DeletePopUp from '../Modals/DeletePopUp'
import ActivatePopUp from '../Modals/ActivatePopUp'
import { getAllUsers, createUser, updateUser, activateUser, deactivateUser, deleteUserPermanent, updatePassword, getAgencyStats, getAgencyList, switchUserAgency, checkSwitchAgency } from '../../requests'
import { notifyError, notifySuccess, getApiErrorMessage } from '../../utils/helper'
import { deleteAuthToken } from '../../utils/helper'
import { removeUserInformation } from '../../_store/reducers/userReducer'
import { validateName, validateEmail } from '../helper/validators'



const LIMIT = 5


const SuperIndex = () => {
  const user = useSelector((state) => state.user)

  const [agencyId, setAgencyId] = useState(null)
  const role = useRole()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [users, setUsers] = useState([])
  const [openActionsId, setOpenActionsId] = useState(null)
  const [dropdownAnchor, setDropdownAnchor] = useState(null)
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
    perPage: LIMIT,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  })
  const [adminStats, setAdminStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  })


  const [showAddUserForm, setShowAddUserForm] = useState(false)
  const [fullName, setFullName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [inviteRole, setInviteRole] = useState('user')
  const [addUserSubmitting, setAddUserSubmitting] = useState(false)

  // Edit User form 
  const [editingUser, setEditingUser] = useState(null)
  const [editName, setEditName] = useState('')
  const [editRole, setEditRole] = useState('user')
  const [editPassword, setEditPassword] = useState('')
  const [showEditPassword, setShowEditPassword] = useState(false)
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [agencies, setAgencies] = useState([])
  const [editAgencyId, setEditAgencyId] = useState(null)
  const [isLastAdmin, setIsLastAdmin] = useState(false)
  const [eligibleReplacements, setEligibleReplacements] = useState([])
  const [replacementAdminId, setReplacementAdminId] = useState(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    if(user.agencyId) {
      setAgencyId(user.agencyId)
    }
    resetAll()
  }, [user.agencyId])
 

  const fetchAdminStats = useCallback(async () => {
  
    try {
      const res = await getAgencyStats()
      if (res?.status<400) {
        const data = res?.data ?? res
        setAdminStats(data?.user)
      }
    } catch (error) {
      console.error('Failed to fetch admin stats', error)
    }
  },[user?.agencyId])

  useEffect(() => {
    fetchAdminStats()
  }, [fetchAdminStats])

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const response = await getAgencyList()
        if (response?.status < 400 && Array.isArray(response?.data)) {
          setAgencies(response.data)
        }
      } catch (error) {
        console.error('Failed to load agencies', error)
      }
    }
    fetchAgencies()
  }, [])

  const handleGetUsers = useCallback(
    async (pageNum) => {
      if (!agencyId || !user.agencyId) return
      const ag_id = user.agencyId ?? agencyId
      setLoading(true)
      try {
        const response = await getAllUsers(
          { page: pageNum, limit: LIMIT, ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }) },
          ag_id
        )
        if (response?.status < 400) {
          const { data = [], pagination: paginationData = {} } =
            response?.data ?? {}
          const p = paginationData
          const total = p.total ?? 0
          const perPageFromApi = p.perPage ?? p.per_page ?? LIMIT
          const totalPages =
            p.totalPages ??
            p.total_pages ??
            Math.max(1, Math.ceil(total / (perPageFromApi || 1)))
          const currentPage = Number(p.currentPage ?? p.current_page ?? pageNum)
          setUsers(data)
          setPagination({
            total,
            totalPages,
            currentPage:Number(currentPage),
            perPage: perPageFromApi,
            hasNextPage: p.hasNextPage ?? p.has_next_page ?? currentPage < totalPages,
            hasPrevPage: p.hasPrevPage ?? p.has_prev_page ?? currentPage > 1,
            nextPage: p.nextPage ?? p.next_page ?? (currentPage < totalPages ? currentPage + 1 : null),
            prevPage: p.prevPage ?? p.prev_page ?? (currentPage > 1 ? currentPage - 1 : null),
          })
        }
      } catch (error) {
        if (error?.response?.status === 401) {
          deleteAuthToken()
          dispatch(removeUserInformation())
          navigate('/login')
          notifyError('Session expired. Please login again.')
        } else {
          notifyError(
            error?.response?.data?.message || 'Failed to load users'
          )
        }
      } finally {
        setLoading(false)
      }
    },
    [agencyId, dispatch, navigate, user.agencyId, debouncedSearch]
  )

  const loadSwitchAgencyCheck = useCallback(async (userId, nextAgencyId, currentAgencyId) => {
    setIsLastAdmin(false)
    setEligibleReplacements([])
    setReplacementAdminId(null)

    const agencyChanged =
      userId &&
      !Number.isNaN(Number(nextAgencyId)) &&
      !Number.isNaN(Number(currentAgencyId)) &&
      Number(nextAgencyId) !== Number(currentAgencyId)

    if (!agencyChanged) return

    try {
      const response = await checkSwitchAgency(userId)
      if (response?.status < 400) {
        const data = response?.data ?? {}
        setIsLastAdmin(Boolean(data.is_last_admin))
        setEligibleReplacements(Array.isArray(data.eligible_replacements) ? data.eligible_replacements : [])
      }
    } catch (error) {
      console.error('Failed to check agency switch requirements', error)
    }
  }, [])

  // Reset to page 1 when agency or perPage changes
  useEffect(() => {
    if (agencyId) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }))
    }
  }, [agencyId])

  // Debounce the search input before it triggers a fetch
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(timer)
  }, [search])

  // Reset to page 1 whenever the (debounced) search term changes
  useEffect(() => {
    setPagination((prev) => (prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }))
  }, [debouncedSearch])

  // Fetch when currentPage or perPage changes (effect drives fetch)
  useEffect(() => {
    handleGetUsers(pagination.currentPage)
  }, [pagination.currentPage, handleGetUsers])

  useEffect(() => {
    if (!editingUser?.id) return
    const currentAgencyId = Number(editingUser.agency?.id ?? agencyId)
    loadSwitchAgencyCheck(editingUser.id, editAgencyId, currentAgencyId)
  }, [editingUser, editAgencyId, agencyId, loadSwitchAgencyCheck])

  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages) return
    setPagination((prev) => ({ ...prev, currentPage: page }))
  }

  const handleAddUserToggle = () => {
    if (editingUser) {
      setEditingUser(null)
      setEditName('')
      setEditRole('user')
      setEditPassword('')
      setShowEditPassword(false)
      return
    }
    setShowAddUserForm((prev) => !prev)
    if (showAddUserForm) {
      setFullName('')
      setInviteEmail('')
      setPassword('')
      setInviteRole('user')
      setShowPassword(false)
    }
  }

  const openEdit = (user) => {
    setOpenActionsId(null)
    setDropdownAnchor(null)
    setShowAddUserForm(false)
    setEditingUser(user)
    setEditName(user.full_name || user.name || '')
    setEditRole(user.role || 'user')
    setEditAgencyId(user.agency?.id ?? agencyId)
    setEditPassword('')
    setShowEditPassword(false)
    setReplacementAdminId(null)
    setIsLastAdmin(false)
    setEligibleReplacements([])
  }

  const closeEdit = () => {
    setEditingUser(null)
    setEditName('')
    setEditRole('user')
    setEditAgencyId(null)
    setEditPassword('')
    setShowEditPassword(false)
    setReplacementAdminId(null)
    setIsLastAdmin(false)
    setEligibleReplacements([])
  }

  const updateUserPassword = async (password) => {
    try {
      const response = await updatePassword({ email: editingUser.email, password: password })
      if (response?.status < 400) {
        return true
      } else {
        return false
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to update password.')
      return false
    }
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    if (!editingUser?.id) return

    if(!validateUpdateForm()) return

    const currentAgencyId = Number(editingUser.agency?.id ?? agencyId)
    const nextAgencyId = Number(editAgencyId)
    const agencyChanged =
      !Number.isNaN(nextAgencyId) &&
      !Number.isNaN(currentAgencyId) &&
      nextAgencyId !== currentAgencyId

    if (agencyChanged && isLastAdmin && !replacementAdminId) {
      notifyError('Please select a replacement admin for this agency before switching.')
      return
    }

    setEditSubmitting(true)
    try {
      if (agencyChanged) {
        try {
          const switchResponse = await switchUserAgency(
            {
              agency_id: nextAgencyId,
              full_name: editName.trim() || editingUser.full_name || editingUser.name,
              role: editRole || editingUser.role,
              replacement_admin_id: isLastAdmin ? replacementAdminId : undefined,
            },
            editingUser.id
          )
          if (switchResponse?.status >= 400) {
            notifyError(switchResponse?.data?.message || 'Failed to switch agency.')
            return
          }
        } catch (switchError) {
          notifyError(getApiErrorMessage(switchError, 'Failed to switch agency.'))
          return
        }
      } else {
        const payload = {
          full_name: editName.trim() || editingUser.full_name || editingUser.name,
          role: editRole || editingUser.role,
          ...(editPassword.trim() && { password: editPassword }),
        }
        const response = await updateUser(payload, editingUser.id)
        if (response?.status >= 400) {
          notifyError(response?.data?.message || 'Failed to update user.')
          return
        }
      }

      if (editPassword?.trim()) {
        const isPasswordUpdated = await updateUserPassword(editPassword)
        if (!isPasswordUpdated) {
          notifyError('Failed to update password.')
          return
        }
      }

      notifySuccess(
        agencyChanged
          ? 'User agency switched successfully.'
          : 'User updated successfully.'
      )
      await handleGetUsers(pagination.currentPage)
      await fetchAdminStats()
      closeEdit()
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to update user.')
    } finally {
      setEditSubmitting(false)
    }
  }
  const validateCreateForm = () => {
    if(!validateName(fullName)) {
      notifyError('Please enter a valid full name without special characters and numbers')
      return false
    }
    if(!validateEmail(inviteEmail)) {
      notifyError('Please enter a valid email address')
      return false
    }
    // if(!password.trim()) {
    //   notifyError('Please enter a password')
    //   return false
    // }
    // if(password.length < 8) {
    //   notifyError('Password must be at least 8 characters')
    //   return false
    // }
    if(!inviteRole.trim()) {
      notifyError('Please select a role')
      return false
    }
    return true
  }

  const validateUpdateForm = () => {
    if(!validateName(editName)) {
      notifyError('Please enter a valid full name without special characters and numbers')
      return false
    }
    if(!validateEmail(editingUser.email)) {
      notifyError('Please enter a valid email address')
      return false
    }
    if(!editRole.trim()) {
      notifyError('Please select a role')
      return false
    }
    if(!editAgencyId) {
      notifyError('Please select an agency')
      return false
    }
    return true
  }
  const handleAddUserSubmit = async (e) => {
    e.preventDefault()
    if (!agencyId) {
      notifyError('Agency context is missing.')
      return
    }

    if (!validateCreateForm()) return
    setAddUserSubmitting(true)
    try {
      const payload = {
        full_name: fullName.trim(),
        email: inviteEmail.trim(),
        password,
        agency_id: Number(agencyId),
        role: inviteRole.trim() || 'user',
      }

      
      const response = await createUser(payload)
      if (response?.status < 400) {
        notifySuccess("User invited successfully, Activation link sent to the user's email" || 'User added successfully.')
        setFullName('')
        setInviteEmail('')
        setPassword('')
        setInviteRole('user')
        setShowPassword(false)
        setShowAddUserForm(false)
        handleGetUsers(pagination.currentPage)
        await fetchAdminStats()
      } else {
        notifyError(response?.data?.message || 'Failed to add user.')
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login')
        notifyError('Session expired. Please login again.')
      } else {
        notifyError(
          error?.response?.data?.message || 'Failed to add user.'
        )
      }
    } finally {
      setAddUserSubmitting(false)
    }
  }

  const [deleteTarget, setDeleteTarget] = useState(null)
  const [activateTarget, setActivateTarget] = useState(null)
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null)

  const openDeactivateConfirm = (user) => {
    setOpenActionsId(null)
    setDropdownAnchor(null)
    setDeleteTarget({ id: user.id, full_name: user.full_name || user.name || 'this user' })
  }

  const openDeleteConfirm = (user) => {
    setOpenActionsId(null)
    setDropdownAnchor(null)
    setPermanentDeleteTarget({ id: user.id, full_name: user.full_name || user.name || 'this user' })
  }

  const openActivateConfirm = (user) => {
    setOpenActionsId(null)
    setDropdownAnchor(null)
    setActivateTarget({ id: user.id, full_name: user.full_name || user.name || 'this user' })
  }

  const handleConfirmDeactivate = async () => {
    if (!deleteTarget?.id) return
    const id = deleteTarget.id
    try {
      const response = await deactivateUser(id)
      if (response?.status < 400) {
        notifySuccess(response?.data?.message || 'User deactivated.')
        setDeleteTarget(null)
        await handleGetUsers(pagination.currentPage)
        await fetchAdminStats()
      } else {
        notifyError(response?.data?.message || 'Failed to deactivate user.')
        setDeleteTarget(null)
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login')
        notifyError('Session expired. Please login again.')
      } else {
        notifyError(error?.response?.data?.message || 'Failed to deactivate user.')
        setDeleteTarget(null)
      }
    }
  }

  const handleConfirmActivate = async () => {
    if (!activateTarget?.id) return
    const id = activateTarget.id
    try {
      const response = await activateUser(id)
      if (response?.status < 400) {
        notifySuccess(response?.data?.message || 'User activated.')
        setActivateTarget(null)
        await handleGetUsers(pagination.currentPage)
        await fetchAdminStats()
      } else {
        notifyError(response?.data?.message || 'Failed to activate user.')
        setActivateTarget(null)
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login')
        notifyError('Session expired. Please login again.')
      } else {
        notifyError(error?.response?.data?.message || 'Failed to activate user.')
        setActivateTarget(null)
      }
    }
  }

  const handleConfirmDelete = async () => {
    if (!permanentDeleteTarget?.id) return
    const id = permanentDeleteTarget.id
    try {
      const response = await deleteUserPermanent(id)
      if (response?.status < 400) {
        notifySuccess(response?.data?.message || 'User permanently deleted.')
        setPermanentDeleteTarget(null)
        await handleGetUsers(pagination.currentPage)
        await fetchAdminStats()
      } else {
        notifyError(response?.data?.message || 'Failed to delete user.')
        setPermanentDeleteTarget(null)
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login')
        notifyError('Session expired. Please login again.')
      } else {
        notifyError(error?.response?.data?.message || 'Failed to delete user.')
        setPermanentDeleteTarget(null)
      }
    }
  }

  const resetAll = () =>{
    setShowAddUserForm(false)
    setEditingUser(null)
    setEditName('')
    setEditRole('user')
    setEditPassword('')
    setShowEditPassword(false)
    setFullName('')
    setInviteEmail('')
    setPassword('')

  }

  const activeCount = users.filter((a) => a.isActive).length

  return (
    <div>
      {/* <Header userType={role} headerTitle="Users" /> */}
      <div className={inviteStyles.page}>
        <div className={inviteStyles.headerRow}>
          <div className={inviteStyles.titleBlock}>
            <h1>Team Members</h1>
            <p>Manage users and their access</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* <BreadcrumbDropdown value={agencyId} onChange={onChangeAgency} options={agencies} /> */}
            <button
              type="button"
              onClick={handleAddUserToggle}
              className={inviteStyles.primaryBtn}
            >
              {editingUser
                ? 'Close Edit Form'
                : showAddUserForm
                  ? 'Close Add User Form'
                  : 'Add User +'}
            </button>
          </div>
        </div>
        <div className={inviteStyles.kpiGrid}>
            <KpiCard
              label="Total members"
              value={adminStats?.total}
              sub={`${activeCount} active in this page`}
              icon="👥"
            />
            <KpiCard
              label="Active members"
              value={adminStats?.active}
              sub="Currently active"
              icon="🔑"
            />
            <KpiCard
              label="Inactive members"
              value={adminStats?.inactive}
              sub="Not active"
              icon="👤"
            />
          </div>

        {showAddUserForm && (
          <Card>
            <CardTitle sub="The user will receive an email with link to set their password">
              Add New User
            </CardTitle>
            <form onSubmit={handleAddUserSubmit}>
              <div className={inviteStyles.formGrid3}>
                <div className={inviteStyles.formField}>
                  <label htmlFor="addUserFullName" className={inviteStyles.label}>
                    Full Name <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <input
                    id="addUserFullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Trevor Phillips"
                    className={inviteStyles.input}
                    required
                  />
                </div>
                <div className={inviteStyles.formField}>
                  <label htmlFor="addUserEmail" className={inviteStyles.label}>
                    Email Address <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <input
                    id="addUserEmail"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="name@company.com"
                    className={inviteStyles.input}
                    required
                  />
                </div>
                {/* <div className={inviteStyles.formField}>
                  <label htmlFor="addUserPassword" className={inviteStyles.label}>
                    Password <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <div className={inviteStyles.passwordWrap}>
                    <input
                      id="addUserPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inviteStyles.input}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className={inviteStyles.passwordToggle}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  </div>
                </div> */}
                <div className={inviteStyles.formField}>
                  <label htmlFor="addUserRole" className={inviteStyles.label}>
                    Role <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <SearchableDrp
                    options={USER_ROLES}
                    value={inviteRole}
                    onChange={(v) => setInviteRole(v)}
                    placeholder="Select role..."
                    icon={<User size={16} color="#6b7a90" />}
                    searchable={false}
                  />
                </div>
              </div>
              <div className={inviteStyles.formActions}>
                <button type="submit" className={inviteStyles.submitBtn} disabled={addUserSubmitting}>
                  <Send size={14} />
                  {addUserSubmitting ? 'Adding...' : 'Add User'}
                </button>
                <button
                  type="button"
                  onClick={handleAddUserToggle}
                  className={inviteStyles.secondaryBtn}
                >
                  Cancel
                </button>
              </div>
            </form>
          </Card>
        )}

        {editingUser && (
          <Card>
            <CardTitle sub={`Editing ${editingUser.email}`}>
              Edit User
            </CardTitle>
            <form onSubmit={handleEditSubmit}>
              <div className={inviteStyles.formGrid2}>
                <div className={inviteStyles.formField}>
                  <label htmlFor="editFullName" className={inviteStyles.label}>
                    Full Name <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <input
                    id="editFullName"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Full name"
                    className={inviteStyles.input}
                    required
                  />
                </div>
                <div className={inviteStyles.formField}>
                  <label className={inviteStyles.label}>Email (read-only)</label>
                  <input
                    type="text"
                    value={editingUser.email || ''}
                    readOnly
                    disabled
                    className={`${inviteStyles.input} ${inviteStyles.inputReadOnly}`}
                  />
                </div>
                <div className={inviteStyles.formField}>
                  <label htmlFor="editAgency" className={inviteStyles.label}>
                    Agency <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <SearchableDrp
                    options={agencies.map((agency) => ({
                      value: agency.id,
                      label: agency.name,
                    }))}
                    value={editAgencyId}
                    onChange={(value) => setEditAgencyId(value)}
                    placeholder="Select agency..."
                    searchable={true}
                  />
                </div>
                {isLastAdmin &&
                  Number(editAgencyId) !== Number(editingUser.agency?.id ?? agencyId) && (
                  <div className={inviteStyles.formField}>
                    <label htmlFor="replacementAdmin" className={inviteStyles.label}>
                      Replacement Admin <span className={inviteStyles.labelRequired}>*</span>
                    </label>
                    <SearchableDrp
                      options={eligibleReplacements.map((member) => ({
                        value: member.id,
                        label: `${member.full_name || member.display_name} (${member.email || member.display_email})${
                          member.isActive === false ? ' — inactive' : ''
                        }`,
                      }))}
                      value={replacementAdminId}
                      onChange={(value) => setReplacementAdminId(value)}
                      placeholder={
                        eligibleReplacements.length
                          ? 'Select new agency admin...'
                          : 'No eligible users in this agency'
                      }
                      searchable={true}
                    />
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>
                      This is the last admin of the current agency. Choose who will become the new admin before switching.
                    </p>
                  </div>
                )}
                <div className={inviteStyles.formField}>
                  <label htmlFor="editRole" className={inviteStyles.label}>
                    Role <span className={inviteStyles.labelRequired}>*</span>
                  </label>
                  <SearchableDrp
                    options={USER_ROLES}
                    value={editRole}
                    onChange={(v) => setEditRole(v)}
                    placeholder="Select role..."
                    icon={<User size={16} color="#6b7a90" />}
                    searchable={false}
                  />
                </div>
                <div className={inviteStyles.formField}>
                  <label htmlFor="editPassword" className={inviteStyles.label}>
                    New Password
                  </label>
                  <div className={inviteStyles.passwordWrap}>
                    <input
                      id="editPassword"
                      type={showEditPassword ? 'text' : 'password'}
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="Leave blank to keep unchanged"
                      className={inviteStyles.input}
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditPassword((p) => !p)}
                      className={inviteStyles.passwordToggle}
                      aria-label={showEditPassword ? 'Hide password' : 'Show password'}
                    >
                      {showEditPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className={inviteStyles.formActions}>
                <button type="submit" className={inviteStyles.submitBtn} disabled={editSubmitting}>
                  <Send size={14} />
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={closeEdit}
                  className={inviteStyles.secondaryBtn}
                >
                  Cancel
                </button>
              </div>
            </form>
          </Card>
        )}

        <Card>
          <CardTitle>All Users</CardTitle>
          <div className={inviteStyles.searchBarWrap}>
            <Search size={16} className={inviteStyles.searchIcon} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className={inviteStyles.searchInput}
            />
          </div>
          <div className={inviteStyles.tableWrap}>
            {loading ? (
              <p className={inviteStyles.loadingText}>Loading users...</p>
            ) : users.length === 0 ? (
              <p className={inviteStyles.emptyText}>No users found</p>
            ) : (
            <table className={inviteStyles.table}>
              
              <thead>
                <TableHeader
                  columns={['Name', 'Email', 'Agency', 'Role', 'Status', 'Actions']}
                />
              </thead>
              <tbody>
                
                {users.map((user) => (
                  <tr key={user.id}>
                    <TableCell bold>
                      {user.full_name || user.name || '—'}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {agencies.find((agency) => String(agency.id) === String(user.agency?.id))?.name ||
                        user.agency?.name ||
                        '—'}
                    </TableCell>
                    <TableCell>
                      <Badge color={user.role === 'admin' ? '#f97316' : '#14B8A6'}>
                        {toCapitalize(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        color={
                          user.is_deleted
                            ? '#ef4444'
                            : user.isActive
                              ? '#22c55e'
                              : '#6b7280'
                        }
                      >
                        {user.is_deleted ? 'Deleted' : user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {!user.is_deleted && (
                        <div className={inviteStyles.actionsCell}>
                          <div className={inviteStyles.dropdownWrap}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                const rect = e.currentTarget.getBoundingClientRect()
                                if (openActionsId === user.id) {
                                  setOpenActionsId(null)
                                  setDropdownAnchor(null)
                                } else {
                                  setDropdownAnchor({
                                    top: rect.bottom,
                                    right: rect.right,
                                    left: rect.left,
                                  })
                                  setOpenActionsId(user.id)
                                }
                              }}
                              className={inviteStyles.actionIcon}
                              title="Actions"
                              aria-haspopup="true"
                              aria-expanded={openActionsId === user.id}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openActionsId === user.id &&
                              dropdownAnchor &&
                              ReactDOM.createPortal(
                                <>
                                  <div
                                    className={inviteStyles.dropdownBackdrop}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setOpenActionsId(null)
                                      setDropdownAnchor(null)
                                    }}
                                    aria-hidden="true"
                                  />
                                  <ul
                                    className={inviteStyles.dropdownMenu}
                                    role="menu"
                                    style={{
                                      top: dropdownAnchor.top + 4,
                                      right:
                                        typeof window !== 'undefined'
                                          ? window.innerWidth - dropdownAnchor.right
                                          : 0,
                                    }}
                                  >
                                    <li role="none">
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className={inviteStyles.dropdownItem}
                                        onClick={() => openEdit(user)}
                                      >
                                        Edit
                                      </button>
                                    </li>
                                    <li role="none">
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className={user.isActive ? inviteStyles.dropdownItemDanger : inviteStyles.dropdownItem}
                                        onClick={() => {
                                          if (user.isActive) openDeactivateConfirm(user)
                                          else openActivateConfirm(user)
                                        }}
                                      >
                                        {user.isActive ? 'Deactivate' : 'Activate'}
                                      </button>
                                    </li>
                                    <li role="none">
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className={inviteStyles.dropdownItemDanger}
                                        onClick={() => openDeleteConfirm(user)}
                                      >
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                </>,
                                document.body
                              )}
                          </div>
                        </div>
                      )}
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
            )
            }
            <div className={inviteStyles.pagination}>
              <p className={inviteStyles.paginationInfo}>
                Showing{' '}
                {pagination.total === 0
                  ? 0
                  : (pagination.currentPage - 1) * pagination.perPage + 1}
                –
                {pagination.total === 0
                  ? 0
                  : Math.min(
                      pagination.currentPage * pagination.perPage,
                      pagination.total
                    )}{' '}
                of {pagination.total}
                {pagination.totalPages > 1 && (
                  <> · Page {pagination.currentPage} of {pagination.totalPages}</>
                )}
              </p>
              {pagination.totalPages > 1 && (
                <div className={inviteStyles.paginationBtns}>
                  <button
                    type="button"
                    className={inviteStyles.pageBtn}
                    onClick={() => goToPage(pagination.prevPage ?? pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    title="Previous page"
                  >
                    <ChevronLeft size={16} style={{ color: 'black' }} />
                  </button>
                  <button
                    type="button"
                    className={inviteStyles.pageBtn}
                    onClick={() => goToPage(pagination.nextPage ?? pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    title="Next page"
                  >
                    <ChevronRight size={16} style={{ color: 'black' }} />
                  </button>
                </div>
              )}
            </div>
          </div>
         
        </Card>

        {/* <Card>
            <CardTitle sub="What each role can do within the platform">Role Permissions</CardTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <TableHeader columns={['Permission', 'Super Admin', 'Agency Admin', 'User']} />
              </thead>
              <tbody>
                {[
                  { perm: 'Manage agencies', sa: true, aa: false, u: false },
                  { perm: 'Manage brands', sa: true, aa: true, u: false },
                  { perm: 'Manage users', sa: true, aa: true, u: false },
                  { perm: 'Create & configure simulations', sa: true, aa: true, u: true },
                  { perm: 'Run simulations', sa: true, aa: true, u: true },
                  { perm: 'View results & insights', sa: true, aa: true, u: true },
                  { perm: 'View cost dashboard', sa: true, aa: true, u: false },
                  { perm: 'Export reports', sa: true, aa: true, u: true },
                ].map((r, i) => (
                  <tr key={i}>
                    <TableCell>{r.perm}</TableCell>
                    <TableCell style={{ textAlign: 'left' }}>
                      <span style={{ color: r.sa ? '#22c55e' : '#ef4444' }}>
                        {r.sa ? '✓' : '✗'}
                      </span>
                    </TableCell>
                    <TableCell style={{ textAlign: 'left' }}>
                      <span style={{ color: r.aa ? '#22c55e' : '#ef4444' }}>
                        {r.aa ? '✓' : '✗'}
                      </span>
                    </TableCell>
                    <TableCell style={{ textAlign: 'left' }}>
                      <span style={{ color: r.u ? '#22c55e' : '#ef4444' }}>
                        {r.u ? '✓' : '✗'}
                      </span>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card> */}
      </div>

      {deleteTarget && (
        <DeletePopUp
          title="Deactivate user"
          message={`Are you sure you want to deactivate "${deleteTarget.full_name}"? They will lose access to the platform.`}
          onClose={() => setDeleteTarget(null)}
          onDelete={handleConfirmDeactivate}
        />
      )}

      {activateTarget && (
        <ActivatePopUp
          title="Activate user"
          message={`Are you sure you want to activate "${activateTarget.full_name}"? They will regain access to the platform.`}
          onClose={() => setActivateTarget(null)}
          onActivate={handleConfirmActivate}
        />
      )}

      {permanentDeleteTarget && (
        <DeletePopUp
          variant="delete"
          title="Delete user"
          message={`This will permanently delete "${permanentDeleteTarget.full_name}" and all of their simulations. This cannot be undone. To move the account to another agency instead, edit the user and change the agency there.`}
          onClose={() => setPermanentDeleteTarget(null)}
          onDelete={handleConfirmDelete}
        />
      )}

    </div>
  )
}

export default SuperIndex
